import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { STUDIO_WORKSPACE_KEY } from "../constants";
import { logWorkspaceChanged } from "../runtime/runtimeLogger";

const WORKSPACE_CHANGED_EVENT = "studio-workspace-changed";

const defaultWorkspace = {
  projectId: "",
  projectName: "",
  connectionId: "",
  connectionName: "",
  environment: "",
  status: "unknown",
};

function loadWorkspace() {
  try {
    const raw = localStorage.getItem(STUDIO_WORKSPACE_KEY);
    if (!raw) return { ...defaultWorkspace };
    return { ...defaultWorkspace, ...JSON.parse(raw) };
  } catch {
    return { ...defaultWorkspace };
  }
}

function saveWorkspace(workspace) {
  try {
    localStorage.setItem(STUDIO_WORKSPACE_KEY, JSON.stringify(workspace));
    window.dispatchEvent(new CustomEvent(WORKSPACE_CHANGED_EVENT, { detail: workspace }));
  } catch {
    // ignore quota errors
  }
}

const StudioWorkspaceContext = createContext(null);

/**
 * Active workspace context — persisted to localStorage.
 * Sprint 2: context only; no secondary Firebase initialization.
 * Sprint 3: Tauri/desktop will consume the same shape for runtime switching.
 */
export function StudioWorkspaceProvider({ children }) {
  const [workspace, setWorkspaceState] = useState(loadWorkspace);

  // Sync workspace when changed in another tab or another provider tree (same tab).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STUDIO_WORKSPACE_KEY) return;
      setWorkspaceState(loadWorkspace());
    };
    const onCustom = (e) => {
      if (e.detail) setWorkspaceState({ ...defaultWorkspace, ...e.detail });
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(WORKSPACE_CHANGED_EVENT, onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(WORKSPACE_CHANGED_EVENT, onCustom);
    };
  }, []);

  const persist = useCallback((next) => {
    setWorkspaceState(next);
    saveWorkspace(next);
    logWorkspaceChanged(next);
  }, []);

  const selectProject = useCallback(
    (project) => {
      if (!project) return;
      persist({
        ...workspace,
        projectId: project.id,
        projectName: project.name || "",
      });
    },
    [workspace, persist]
  );

  const selectConnection = useCallback(
    (connection, projectName) => {
      if (!connection) return;
      persist({
        projectId: connection.projectId || workspace.projectId,
        projectName: projectName || workspace.projectName,
        connectionId: connection.id,
        connectionName: connection.name || "",
        environment: connection.environment || "",
        status: connection.status || "unknown",
      });
    },
    [workspace, persist]
  );

  const setEnvironment = useCallback(
    (environment) => {
      persist({ ...workspace, environment });
    },
    [workspace, persist]
  );

  const setStatus = useCallback(
    (status) => {
      persist({ ...workspace, status });
    },
    [workspace, persist]
  );

  const clearWorkspace = useCallback(() => {
    persist({ ...defaultWorkspace });
  }, [persist]);

  const clearWorkspaceConnection = useCallback(() => {
    persist({
      ...workspace,
      connectionId: "",
      connectionName: "",
      environment: "",
      status: "unknown",
    });
  }, [workspace, persist]);

  const value = useMemo(
    () => ({
      workspace,
      selectProject,
      selectConnection,
      setEnvironment,
      setStatus,
      clearWorkspace,
      clearWorkspaceConnection,
    }),
    [workspace, selectProject, selectConnection, setEnvironment, setStatus, clearWorkspace, clearWorkspaceConnection]
  );

  return <StudioWorkspaceContext.Provider value={value}>{children}</StudioWorkspaceContext.Provider>;
}

export function useStudioWorkspace() {
  const ctx = useContext(StudioWorkspaceContext);
  if (!ctx) throw new Error("useStudioWorkspace must be used within StudioWorkspaceProvider");
  return ctx;
}
